/**
 * React Query hooks for platform delays.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platformDelaysApi } from '../api/platform-delays';
import type { UpdatePlatformDelayRequest } from '../types';

/**
 * Query keys for platform delays.
 */
export const platformDelayKeys = {
  all: (siteSlug: string) => ['platform-delays', siteSlug] as const,
};

/**
 * Hook to list platform delays for a site.
 */
export function usePlatformDelays(siteSlug: string) {
  return useQuery({
    queryKey: platformDelayKeys.all(siteSlug),
    queryFn: async () => {
      const response = await platformDelaysApi.list(siteSlug);
      return response.data;
    },
    enabled: !!siteSlug,
  });
}

/**
 * Hook to update a platform delay.
 */
export function useUpdatePlatformDelay(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      platform,
      data,
    }: {
      platform: string;
      data: UpdatePlatformDelayRequest;
    }) => {
      return platformDelaysApi.update(siteSlug, platform, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: platformDelayKeys.all(siteSlug) });
    },
  });
}

/**
 * Hook to reset a platform delay to default.
 */
export function useResetPlatformDelay(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (platform: string) => {
      return platformDelaysApi.reset(siteSlug, platform);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: platformDelayKeys.all(siteSlug) });
    },
  });
}
